
  import Papa from "papaparse";
  import { Vue, Component, Prop, Watch, Model } from "vue-property-decorator";

  import FormGroup from "@/components/form/FormGroup.vue";
  import UiButton from "@/components/button/ui-button.vue";
  import TableContenteditable from "@/components/table/table-contenteditable.vue";
  import UserInvitationData from "@/components/invite/UserInvitationData.vue";
  import ModalSectionsDivider from "@/components/modal/ModalSectionsDivider.vue";
  import { isEqual } from "lodash-es";
  import ParsedCSV from "@/entities/ParsedCSV";
  import { NOTIFICATION } from "@/entities/Events";
  import Message, { MessageDuration } from "@/data/notificationsMessages";
  import PicturePlaceholderIcon from "@/components/icons/PicturePlaceholderIcon.vue";



  interface UserInterface {
    id: string | null;
    email: string | null;
    givenName: string | null;
    familyName: string | null;
    valid: boolean;
    processing: boolean;
  }

  @Component({
    components: {
      PicturePlaceholderIcon,
      TableContenteditable,
      FormGroup,
      UiButton,
      UserInvitationData,
      ModalSectionsDivider,
    },
  })
  export default class CsvUploader extends Vue {
    @Prop({ type: String, required: true }) readonly companyId!: string;
    @Prop({ type: Boolean, default: false }) readonly csvUploadCompleted!: boolean;
    @Prop({ type: Boolean, default: false }) readonly loadingFinished!: boolean;
    @Prop({ type: Boolean, default: false }) readonly isManager!: boolean;
    @Prop({ type: Number, default: 0 }) readonly availableNumberOfManagers!: number;
    @Prop({ type: Boolean, default: false }) readonly usersUnlimited!: boolean;
    @Model("parsedCsvChange", { type: Array }) readonly csvParseOutput!: UserInterface[];

    $papa: any;
    requestedUser: any;
    loading: boolean = false;
    csvFilename: string = "";

    deleteCsvEntry(index: number): void {
      const value = [...this.csvParseOutput];
      value.splice(index, 1);
      this.$emit("parsedCsvChange", value);
    }

    handleCsvSubmit(): void {
      const userObjects = this.csvParseOutput.map((u) => {
        return {
          id: u.id || null,
          givenName: u.givenName || "",
          familyName: u.familyName || "",
          email: u.email,
          processing: false,
          valid: false,
        };
      });
      this.$emit("csvUploadUsers", userObjects);
      this.loading = true;
    }

    isDeleteAllowed(csvParseOutput: UserInterface[]): boolean {
      return csvParseOutput.length > 1;
    }

    @Watch("csvUploadCompleted")
    clearParseOutput(): void {
      if (this.csvUploadCompleted) {
        this.$emit("parsedCsvChange", []);
      }
    }

    @Watch("loadingFinished")
    setLoading(): void {
      this.loading = this.loadingFinished;
    }

    downloadSampleCsv(): void {
      //@ts-ignore
      this.$refs.sample.href = "data:csv/plain;charset=utf-8," + encodeURIComponent("email,given_name,family_name");
      //@ts-ignore
      this.$refs.sample.download = "jobbliss_invite_sample.csv";
    }

    handleFileUpload(): void {
      (this.$refs.fileUpload as Vue & { click: () => boolean }).click();
    }

    async addFile(event: any): Promise<void> {
      const file = event.dataTransfer ? event.dataTransfer.files[0] : event.target.files[0];

      this.csvFilename = file.name;
      const extention = file.name.split(".")[1];
      if (extention === "csv") {
        Papa.parse(file, {
          header: true,
          complete: (parsedCSV: ParsedCSV) => {
            const tableHeaders = ["email", "given_name", "family_name"];
            const headersValid = isEqual(tableHeaders, parsedCSV.meta.fields);

            if (!headersValid) {
              this.$emit("invalidHeaderRow");
              return;
            }

            const data = parsedCSV.data || [];

            if (data.length === 0) {
              this.$eventBus.emit(
                NOTIFICATION,
                Message.errorMessage(
                  "The uploaded file is empty",
                  MessageDuration.MEDIUM
                ),
              );
              return;
            }

            const result = data
              .filter(entry => entry.email !== "")
              .map(({email, given_name, family_name}) => ({
                email,
                familyName: family_name,
                givenName: given_name,
              }))

            this.$emit("parsedCsvChange", result);
          },
        });
      } else {
        this.$emit("wrongExtension");
      }
    }

    isAddingUserAllowed(userIndex: number): boolean {
      if (this.usersUnlimited) {
        return true;
      }

      const availableManagersLeft = this.availableNumberOfManagers - userIndex;
      return availableManagersLeft > 0
    }

    get showCsv(): boolean {
      return this.csvParseOutput.length > 0;
    }

    handleClose() {
      this.$emit("close", false);
      this.$emit("clearCsv");
    }
  }
